import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as sentry from '@sentry/react';

import { PlatformApp, Schema as SchemaType } from '@typings';
import { PATH } from '@constants';
import { toast } from '@features';
import { getPlatformAppsTemplate, installPlatformApp } from '@services';
import { contextNamesSelector } from '@selectors';
import { useHelmetTitle, useSelector } from '@hooks';
import {
  extractSchema,
  flattenObject,
  formatToCamelCase,
  invariant,
  isArray,
  path,
  toastifyResponseError,
} from '@utils';

import { ChaseSpinner, Helmet } from '@components';
import { Layout } from '@components/Layouts';
import { Schema, SchemaRef } from '@components/Schema';

type ResponseError = {
  message: string;
  path: string[];
};

const JOB_TEMPLATE_NAME = 'job';

const META_DISPLAY_NAME: SchemaType.Model = {
  children: [
    {
      type: SchemaType.Type.Field,
      name: 'displayName',
      properties: {
        type: 'string',
        name: 'displayName',
        title: 'Metadata',
        description: 'App display name',
      },
    },
  ],
  type: SchemaType.Type.Container,
  name: 'displayName',
  title: 'Metadata',
  description: 'App display name',
};

export default function JobConstructorPage() {
  const navigate = useNavigate();
  const { makeTitle } = useHelmetTitle();

  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [template, setTemplate] = useState<PlatformApp.Template>();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        const template = await getPlatformAppsTemplate(
          {
            clusterName,
            projectName,
            organizationName,
            version: 'latest',
            name: JOB_TEMPLATE_NAME,
          },
          { formatResponse: false },
        );

        if (!template) {
          setIsLoading(false);
          sentry.captureMessage('Required template job not found');
          toastifyResponseError('Required template job not found');
          return;
        }

        setTemplate(template);
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schemaRef = useRef<SchemaRef>(null);

  const uiSchemas = useMemo(() => {
    if (!template) {
      return [];
    }

    const schema = extractSchema(template.input);
    return [...schema.uiSchemas, META_DISPLAY_NAME];
  }, [template]);

  const { methods } = schemaRef.current ?? {};

  const { title, logo, version, name } = useMemo(() => {
    if (!template) {
      return {};
    }

    return formatToCamelCase({
      logo: template.logo,
      name: template.name,
      title: template.title,
      version: template.version,
    });
  }, [template]) as Pick<
    PlatformApp.Template,
    'logo' | 'name' | 'title' | 'version'
  >;

  const handleResponseErrors = (errors: ResponseError[]) => {
    const formValues = flattenObject(methods?.getValues() ?? {});

    errors.forEach(({ message, path }) => {
      const fieldName = path.join('.');

      /**
       * If field name is registered in schema, attach error message to field.
       * Otherwise, toastify error message
       */
      if (fieldName in formValues) {
        methods?.setError(fieldName, { message });
      } else {
        const toastMessage = fieldName ? `${fieldName}: ${message}` : message;

        toast.error(toastMessage);
      }
    });
  };

  const handleFormSubmit = async ({ displayName, ...input }: FieldValues) => {
    try {
      setIsLoading(true);

      invariant(organizationName);
      invariant(clusterName);
      invariant(projectName);

      const { outputParams } = await installPlatformApp({
        organizationName,
        clusterName,
        projectName,
        payload: {
          input,
          display_name: displayName,
          template_name: JOB_TEMPLATE_NAME,
        },
      });

      toast.success('Job is being installed');

      if (outputParams?.jobId) {
        navigate(path.job(outputParams?.jobId));
      } else {
        navigate(PATH.JOBS);
      }
    } catch (error) {
      const { message, detail, errors } = error as {
        message?: string;
        detail?: ResponseError;
        errors?: ResponseError[];
      };

      if (isArray(errors)) {
        handleResponseErrors(errors);
      }

      toast.error(message ?? detail?.message ?? 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const getContent = () => {
    if (!template && !isLoading) {
      return (
        <div className="flex w-full items-center justify-center">
          <p>No job template found</p>
        </div>
      );
    }

    if (!template) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-8 w-8" />
        </div>
      );
    }

    return (
      <Schema
        ref={schemaRef}
        logo={logo}
        name={name}
        title={title}
        version={version}
        loading={isLoading}
        schema={uiSchemas}
        onSubmit={handleFormSubmit}
      />
    );
  };

  return (
    <Layout title="Create New Job" className="flex w-full gap-10">
      <Helmet
        title={makeTitle('Create Job', 'Jobs', '%p', '%c')}
        description="Set up and customize jobs with specific parameters using our intuitive job constructor. Tailor each job to your needs and streamline your workflow with personalized configurations"
      />

      <Layout.Content className="h-full">{getContent()}</Layout.Content>
    </Layout>
  );
}
